import { useInventoryStore } from "../stores/inventory";

const INVENTORY_ITEMS = new Set(["dewars", "racks", "pucks", "boxes"]);

function canonicalId(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id || null;
}

function activeLabId(store) {
  return canonicalId(store.state.activeUser?.activeLabId) || canonicalId(store.state.activeUser?.labId);
}

function currentSnapshotMatchesActiveLab(store) {
  const currentLabId = activeLabId(store);
  if (!currentLabId) return false;
  if (!Array.isArray(store.state.dewarData) || !store.state.dewarData.length) return false;
  return store.state.dewarData.some((entry) => canonicalId(entry?.labId) === currentLabId);
}

function shouldShortCircuitUpdate(store, payload) {
  const { item, forpayload, body } = payload;
  if (!forpayload || !body) return false;

  const currentDewar =
    store.state.dewarData?.[forpayload.labIndex]?.data?.[forpayload.dewarIndex];
  if (!currentDewar) return false;

  if (item === "dewars") {
    return currentDewar?.[forpayload.propertytoupdate] === body[forpayload.propertytoupdate];
  }

  if (item === "racks") {
    return (
      currentDewar?.racks?.[forpayload.rackIndex]?.[forpayload.propertytoupdate] ===
      body[forpayload.propertytoupdate]
    );
  }

  if (item === "pucks") {
    return (
      currentDewar?.racks?.[forpayload.rackIndex]?.pucks?.[forpayload.puckIndex]?.[
        forpayload.propertytoupdate
      ] === body[forpayload.propertytoupdate]
    );
  }

  if (item === "boxes" && forpayload.propertytoupdate !== "gridProperty") {
    return (
      currentDewar?.racks?.[forpayload.rackIndex]?.pucks?.[forpayload.puckIndex]?.[
        "well" + forpayload.wellNo
      ]?.[forpayload.propertytoupdate] === body[forpayload.propertytoupdate]
    );
  }

  return false;
}

function refreshLegacyDewarData(store) {
  store.commit("updateDewarData", { value: store.state.dewarData });
}

function refreshInventoryUi(store, payload = {}) {
  refreshLegacyDewarData(store);
  store.state.refreshtransport = true;

  if (payload.body?.moveTo && payload.samepuck) {
    store.state.moveTo = payload.body.moveTo.toWell;
  }

  if (!payload.skipVisualRefresh) {
    store.state.gridChangedPuckVisualTrigger = true;
  }
}

async function fetchPermissionSnapshot(store) {
  const url = `${store.state.hostname}/api/v1/dewars`;
  const permissionsUrl = `${store.state.hostname}/api/v1/dewars/permissions`;
  const request = new Request(url, {
    credentials: "include",
    method: "GET",
    headers: { "content-type": "application/json" },
  });
  const permissionsRequest = new Request(permissionsUrl, {
    credentials: "include",
    method: "GET",
    headers: { "content-type": "application/json" },
  });

  const [response, permissionsResponse] = await Promise.all([
    fetch(request),
    fetch(permissionsRequest),
  ]);
  const [data, permissions] = await Promise.all([
    response.json(),
    permissionsResponse.json(),
  ]);
  if (data.status === "fail" || data.status === "error") {
    throw new Error(data.message || "Failed to load inventory");
  }
  if (permissions.status === "fail" || permissions.status === "error") {
    throw new Error(permissions.message || "Failed to load inventory permissions");
  }

  store.commit("updateDewarData", {
    value: data.data.data,
    permissions: permissions.data.data,
  });
  return data.data.data;
}

export default {
  methods: {
    async ensureInventorySnapshotLoaded({ force = false } = {}) {
      refreshLegacyDewarData(this.appStore);

      if (!force) {
        if (this.appStore.state.activeUser?.role === "lab-admin") {
          return this.appStore.state.dewarData;
        }

        if (currentSnapshotMatchesActiveLab(this.appStore)) {
          return this.appStore.state.dewarData;
        }
      }

      return fetchPermissionSnapshot(this.appStore);
    },

    refreshInventoryUi() {
      refreshLegacyDewarData(this.appStore);
    },

    async inventoryCreateItem(payload) {
      if (!INVENTORY_ITEMS.has(payload.item)) {
        throw new Error(`Unsupported inventory item: ${payload.item}`);
      }

      const inventoryStore = useInventoryStore();
      const { item, body, dewarId } = payload;
      let createdId = null;

      if (item === "dewars") createdId = await inventoryStore.createDewar(body);
      else if (item === "racks") {
        createdId = await inventoryStore.createRack({ ...body, dewarId: body.dewarId || dewarId || null });
      } else if (item === "pucks") createdId = await inventoryStore.createPuck(body);
      else if (item === "boxes") createdId = await inventoryStore.createBox(body);

      refreshInventoryUi(this.appStore, payload);

      if (item === "boxes" && createdId) {
        return inventoryStore.getBox(createdId);
      }

      return `${item} added`;
    },

    async inventoryUpdateItem(payload) {
      if (!INVENTORY_ITEMS.has(payload.item)) {
        throw new Error(`Unsupported inventory item: ${payload.item}`);
      }

      if (shouldShortCircuitUpdate(this.appStore, payload)) {
        return;
      }

      const inventoryStore = useInventoryStore();
      const { item, objid, body } = payload;

      if (item === "dewars") await inventoryStore.updateDewar(objid, body);
      else if (item === "racks") await inventoryStore.updateRack(objid, body);
      else if (item === "pucks") {
        await inventoryStore.updatePuck(objid, {
          ...body,
          rackId: body?.rackId ?? body?.rackid ?? body?.rackID,
        });
      } else if (item === "boxes") await inventoryStore.updateBox(objid, body);

      refreshInventoryUi(this.appStore, payload);
      return "ok";
    },

    async inventoryDeleteItem(payload) {
      if (!INVENTORY_ITEMS.has(payload.item)) {
        throw new Error(`Unsupported inventory item: ${payload.item}`);
      }

      const inventoryStore = useInventoryStore();
      const { item, objid } = payload;

      if (item === "dewars") await inventoryStore.deleteDewar(objid);
      else if (item === "racks") await inventoryStore.deleteRack(objid);
      else if (item === "pucks") await inventoryStore.deletePuck(objid);
      else if (item === "boxes") await inventoryStore.deleteBox(objid);

      refreshLegacyDewarData(this.appStore);
    },

    async assignRackToDewar(payload) {
      const inventoryStore = useInventoryStore();
      await inventoryStore.assignRackToDewar(payload.rackid, payload.removeRack ? null : payload.dewarid);
      refreshLegacyDewarData(this.appStore);
    },

    async assignPuckToRack(payload) {
      const inventoryStore = useInventoryStore();
      await inventoryStore.assignPuckToRack(payload.puckid, payload.rackid);
      refreshLegacyDewarData(this.appStore);

      if (payload.fromtransport) {
        this.appStore.state.transportUpdate = true;
      }

      if (payload.fromtransport) {
        return;
      }

      const currentLabId = canonicalId(this.appStore.state.activeUser?.labId);
      const nextLabId = payload.lab || currentLabId;

      if (!nextLabId || nextLabId === currentLabId) {
        this.appStore.commit("updateUnassignedPucks", {
          value: inventoryStore.getUnassignedPucks,
        });
        return;
      }

      await this.appStore.dispatch("getUnassignedPucks", { lab: nextLabId });
    },
  },
};
